import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { RootState } from '../../../redux/Store';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { projectSlice } from '../../../redux/ProjectSlice';
import FilterField from '../../Drawers/BottomDrawer/FilterField';
import BasicSnackBar from '../../Snackbars/BasicSnackBar';
import { useWidgetHeader } from './WidgetHeaderContext';
import { useTranslation } from 'react-i18next';

async function copyTextToClipboard(text: string): Promise<boolean> {
  if (!navigator.clipboard?.writeText) {
    return false;
  }

  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy text: ', err);
    return false;
  }
}

const ConsoleWidget = () => {
  const { consoleDisplay } = useSelector((state: RootState) => state.project);
  const [displayedConsoleLogs, setDisplayedConsoleLogs] = useState<Array<string>>([]);
  const [searched, setSearched] = useState('');
  const [copiedOpen, setCopiedOpen] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const headerTitle = useMemo(() => t('widgetBase.console', 'Console Logs'), [t]);

  const handleClearConsole = useCallback(() => {
    dispatch(projectSlice.actions.onClearConsoleDisplay());
  }, [dispatch]);

  const requestConsoleLogsSearch = useCallback(
    (searchedVal: string) => {
      const filteredRows = consoleDisplay.filter((log: string) => {
        return log.toLowerCase().includes(searchedVal.toLowerCase());
      });
      setDisplayedConsoleLogs(filteredRows);
    },
    [consoleDisplay]
  );

  const handleInput = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setSearched(event.target.value);
      requestConsoleLogsSearch(event.target.value);
    },
    [requestConsoleLogsSearch]
  );

  const cancelSearch = useCallback(() => {
    setSearched('');
    requestConsoleLogsSearch('');
  }, [requestConsoleLogsSearch]);

  useEffect(() => {
    requestConsoleLogsSearch(searched);
  }, [consoleDisplay, requestConsoleLogsSearch, searched]);

  const listRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [displayedConsoleLogs]);

  const consoleText = useMemo(() => displayedConsoleLogs.join('\n'), [displayedConsoleLogs]);

  const handleCopyConsole = useCallback(async () => {
    if (await copyTextToClipboard(consoleText)) {
      setCopiedOpen(true);
    }
  }, [consoleText]);

  const toolbarEnd = useMemo(
    () => (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
        <FilterField searched={searched} handleInput={handleInput} cancelSearch={cancelSearch} />
        <Tooltip title={t('bottomDrawer.tooltip.copy-console', 'Copy Console Logs')}>
          <IconButton
            aria-label={t('aria-label.copy-console', 'Copy console logs')}
            onClick={() => void handleCopyConsole()}
            color="inherit"
            sx={{ px: 0 }}
          >
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('bottomDrawer.tooltip.clear-all', 'Clear All')}>
          <IconButton aria-label={t('aria-label.clear-console', 'clear console')} onClick={handleClearConsole} color="inherit" sx={{ px: 0 }}>
            <ClearAllIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    ),
    [cancelSearch, handleClearConsole, handleCopyConsole, handleInput, searched, t]
  );

  useWidgetHeader({ title: headerTitle, toolbarEnd }, [headerTitle, toolbarEnd]);

  return (
    <>
      <Stack overflow="auto" direction="column" alignItems="flex-start" pt={1} flex={1} minHeight={0} width="100%" ref={listRef}>
        {displayedConsoleLogs.length > 0 && (
          <Typography
            component="div"
            sx={{
              pl: 1,
              pr: 1,
              width: '100%',
              pointerEvents: 'auto',
              userSelect: 'text',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {consoleText}
          </Typography>
        )}
      </Stack>
      <BasicSnackBar
        message={t('shareCodeInfoDialog.snackbar.copied-to-clipboard', 'Copied to Clipboard')}
        open={copiedOpen}
        onClose={setCopiedOpen}
        vertical="bottom"
        horizontal="center"
      />
    </>
  );
};

export default ConsoleWidget;
